import { ORDER_CREATE_FAIL, ORDER_CREATE_REQUEST, ORDER_CREATE_SUCCESS, ORDER_PAYMENT_SUCCESS } from "../Constants/orderConstants";
import { CART_EMPTY } from "../Constants/cartConstants";
import { firestore, firebaseAuth } from "../firebase";
import { collection, addDoc, serverTimestamp, doc, setDoc, updateDoc, deleteDoc, getDocs, getDoc, query, where, increment } from 'firebase/firestore';
import { createOrderPlacedNotification } from '../utils/notificationUtils';

// Generate Android-compatible 24-character ID
// Android logic: UUID.randomUUID().toString().substring(0, 28).replaceAll("-", "").toUpperCase()
// Standard UUID v4 has hyphens at positions 8,13,18,23. Removing them from the first 28 chars yields 24 chars.
const generateOrderId = () => {
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
  return uuid.substring(0, 28).replace(/-/g, '').toUpperCase();
};

// Commission resolution helpers with simple in-memory cache
const commissionCache = {
  subcat: {}, // key: `${category}::${subcategory}` -> number
  cat: {},    // key: `${category}` -> number
};

const fetchCommissionFromSubcategory = async (category, subcategory) => {
  try {
    if (!category || !subcategory) return null;
    const key = `${category}::${subcategory}`;
    if (commissionCache.subcat[key] !== undefined) return commissionCache.subcat[key];
    const subcolRef = collection(firestore, 'CATEGORIES', category, 'SUBCATEGORIES');
    const q = query(subcolRef, where('subCategoryName', '==', subcategory));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const data = snap.docs[0].data() || {};
      const pct = Number(data.commissionRate);
      if (Number.isFinite(pct)) {
        commissionCache.subcat[key] = pct;
        return pct;
      }
    }
    commissionCache.subcat[key] = null;
    return null;
  } catch (e) {
    console.warn('fetchCommissionFromSubcategory failed:', e?.message || e);
    return null;
  }
};

const fetchCommissionFromCategory = async (category) => {
  try {
    if (!category) return null;
    const key = `${category}`;
    if (commissionCache.cat[key] !== undefined) return commissionCache.cat[key];
    const catRef = doc(firestore, 'CATEGORIES', category);
    const snap = await getDoc(catRef);
    if (snap.exists()) {
      const data = snap.data() || {};
      const pct = Number(data.commissionRate);
      if (Number.isFinite(pct)) {
        commissionCache.cat[key] = pct;
        return pct;
      }
    }
    commissionCache.cat[key] = null;
    return null;
  } catch (e) {
    console.warn('fetchCommissionFromCategory failed:', e?.message || e);
    return null;
  }
};

// Generate 6-digit delivery code
const generateDeliveryCode = () => {
  return Math.floor(100000 + Math.random() * 900000);
};

// Calculate commission and marketplace fees
const calculateMarketplaceFees = (itemPrice, commissionPercentage) => {
  // Ensure we have valid numbers
  const price = Number(itemPrice) || 0;
  const commission = Number(commissionPercentage) || 0;
  
  const commissionFee = (price * commission) / 100;
  const collectionFee = price * 0.02; // 2% collection fee
  const totalMarketplaceFee = commissionFee + collectionFee;
  const gstOnMarketplaceFee = commissionFee * 0.18; // 18% GST on commission only
  const totalDeductions = commissionFee + gstOnMarketplaceFee;
  const settlementValue = price - totalDeductions;
  
  return {
    commissionFee: Number(commissionFee.toFixed(2)),
    collectionFee: Number(collectionFee.toFixed(2)),
    totalMarketplaceFee: Number(totalMarketplaceFee.toFixed(2)),
    gstOnMarketplaceFee: Number(gstOnMarketplaceFee.toFixed(2)),
    totalDeductions: Number(totalDeductions.toFixed(2)),
    settlementValue: Number(settlementValue.toFixed(2))
  };
};

// Create order with comprehensive database structure
export const createOrder = (order) => async (dispatch, getState) => {
  try {
    console.log('🚀 createOrder action started');
    dispatch({ type: ORDER_CREATE_REQUEST });
    console.log('📤 ORDER_CREATE_REQUEST dispatched');
    const state = getState();
    const userId = state?.userSignin?.userInfo?.uid || firebaseAuth.currentUser?.uid || 'guest';
    // Generate 24-character order ID (Android-compatible)
    const orderId = generateOrderId();
    const orderDocRef = doc(firestore, 'ORDERS', orderId);

    // Fetch user profile details for flags like isBankDetailAvailable and GST/Business Proof
    let userDetails = {};
    try {
      const userDoc = await getDoc(doc(firestore, 'USERS', userId));
      if (userDoc.exists()) userDetails = { ...userDetails, ...(userDoc.data() || {}) };
      const profileDoc = await getDoc(doc(firestore, 'USERS', userId, 'USER_DATA', 'PROFILE'));
      if (profileDoc.exists()) userDetails = { ...userDetails, ...(profileDoc.data() || {}) };
    } catch (e) {
      console.error('Failed to fetch user profile for order:', e);
    }

    const shipping = state.cart.shippingAddress || {};
    // Prefer items passed from caller (e.g., PlaceOrderScreen filtered list)
    const items = (order && Array.isArray(order.orderItems) && order.orderItems.length > 0)
      ? order.orderItems
      : (state.cart.cartItems || []);

    if (items.length === 0) {
      throw new Error('Cart is empty');
    }

    // Calculate totals
    const totalItems = items.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
    const totalItemPrice = items.reduce((sum, item) => sum + (Number(item.product_price) || 0) * (Number(item.qty) || 0), 0);
    // Shipping disabled: always zero
    const deliveryPrice = 0;
    const savedAmount = items.reduce((sum, item) => {
      const cutted = Number(item.cutted_price) || 0;
      const current = Number(item.product_price) || 0;
      return sum + ((cutted - current) * (Number(item.qty) || 0));
    }, 0);
    const totalAmount = totalItemPrice;
    const totalWeight = items.reduce((sum, item) => sum + (Number(item.weight) || 0.5) * (Number(item.qty) || 0), 0);

    const paymentMethod = state.cart.paymentMethod || order.paymentMethod || 'COD';
    const isCOD = /cod/i.test(paymentMethod);

    // Create main order document with initial "Cancelled" status
    // Resolve a seller id at header level from first item if present
    const headerSellerId = (items[0]?.seller_id || items[0]?.sellerId || items[0]?.seller || order?.seller_id || 'UNKNOWN_SELLER');

    const orderData = {
      Total_Items: totalItems,
      "Total_Item Price": totalItemPrice,
      Delivery_Price: deliveryPrice,
      Total_Amount: totalAmount,
      Saved_Amount: savedAmount,
      Total_Weight: totalWeight,
      Payment_Status: "Not Paid",
      Order_Status: "Cancelled", // Initial status - updated after payment
      Payment_Method: isCOD ? "COD" : (paymentMethod?.toUpperCase?.() ),
      Seller_Id: headerSellerId,
      User_Id: userId,
      Created_Date: serverTimestamp(),
      // User flags
      isBankDetailAvailable: Boolean(
        userDetails.isBankDetailAvailable ?? userDetails.bankDetailsAvailable ?? false
      ),
      customerGst: (
        userDetails.gstNo || userDetails.GSTNo || userDetails.business_gst || userDetails.Business_Proof_No || userDetails.business_proof_no || ""
      ),
    };

    await setDoc(orderDocRef, orderData);

    // Create detailed order items
    for (const item of items) {
      const deliveryCode = generateDeliveryCode();
      const itemTotal = (Number(item.product_price) || 0) * (Number(item.qty) || 0);
      const categoryName = item.category || '';
      const subcatName = item.subCategory || item.subcategory || '';
      let commissionPct = await fetchCommissionFromSubcategory(categoryName, subcatName);
      let fees;
      
      if (!Number.isFinite(commissionPct)) {
        const commissionPercentage = await fetchCommissionFromCategory(categoryName);
        console.log('Commission data for', categoryName, ':', commissionPercentage);
        fees = calculateMarketplaceFees(item.product_price * item.qty, commissionPercentage || 0);
        console.log('Calculated fees:', fees);
        commissionPct = commissionPercentage || 0;
      } else {
        fees = calculateMarketplaceFees(itemTotal, commissionPct);
      }
      
      const itemSellerId = (item.seller_id || item.sellerId || item.seller || headerSellerId || 'UNKNOWN_SELLER');
      
      // Compute seller settlement for this line item from calculated fees
      // fees.settlementValue corresponds to the settlement for the price basis used above
      const sellerSettlement = Math.max(0, Number((fees && fees.settlementValue !== undefined) ? fees.settlementValue : 0));
      
      const orderItem = {
        // Product Information
        Category: item.category || "",
        SubCategory: item.subcategory || "",
        Order_Id: orderId,
        Product_Id: item.product,
        Product_Image: item.product_image_1,
        Product_Title: item.product_title,
        Product_Quantity: Number(item.qty) || 1,
        Product_price: String(item.product_price),
        // Seller_price should reflect the settlement that the seller will receive (non-negative)
        Seller_price: String(Number(sellerSettlement.toFixed(2))),
        Cutted_Price: String(item.cutted_price || item.product_price),

        // Variant Extras
        Size: item.selected_size || item.size || "",
        Color: item.selected_color || item.color || "",

        // Coupon & Discount Information
        Coupon_Id: item.coupon_id || "",
        Discounted_Price: item.discounted_price || "",
        Free_Coupons: Number(item.free_coupons) || 0,

        // Timestamps (All set to serverTimestamp initially)
        Ordered_Date: serverTimestamp(),
        Packed_Date: serverTimestamp(),
        Shipped_Date: serverTimestamp(),
        Delivered_Date: serverTimestamp(),
        Cancelled_Date: serverTimestamp(),

        // Status Fields (Initial Values)
        Payment_Method: isCOD ? "COD" : (paymentMethod?.toUpperCase?.() ),
        Payment_Status: "Not Paid",
        Order_Status: "Cancelled", // Updated after payment
        Return_Status: "No",

        // Customer Information
        User_Id: userId,
        FullName: shipping.name || "",
        Mobile_No: shipping.mobileNo || "",
        Alternate_No: shipping.alternateMobileNo || null,
        Email: shipping.email || "",
        Address: `${shipping.flatNo || shipping.flatno || ""}, ${shipping.locality || ""}${shipping.landmark ? ", " + shipping.landmark : ""}`,
        City: shipping.city || "",
        State: shipping.state || "",
        Pincode: shipping.pinCode || shipping.pincode || "",

        // Financial Information
        Total_Amount: totalAmount,
        Saved_Amount: savedAmount,
        Delivery_Price: deliveryPrice,

        // Logistics Information
        Delivery_Code: deliveryCode,
        isCompanyDelivery: false,
        Total_Weight: Number(item.weight) || 0.5,
        length: Number(item.length) || 15,
        breadth: Number(item.breadth) || 8,
        height: Number(item.height) || 2,
        fixedShippingCharges: deliveryPrice,

        // Business Information
        Seller_Id: itemSellerId,
        
        // Seller Payment Fields
        isPaidToSeller: false,
        SellerTransaction_No: "",
        SellerUTR_No: "",

        // User flags
        isBankDetailAvailable: Boolean(
          userDetails.isBankDetailAvailable ?? userDetails.bankDetailsAvailable ?? false
        ),
        customerGst: (
          userDetails.Business_Proof_No || userDetails.business_proof_no || ""
        ),
        // Request Status
        Cancellation_Requested: false,
        Return_Requested: false,
        
        // Commission & Fee Calculations
        commissionInPercentage: String(commissionPct || 0),
        CommissionFee: (fees && fees.commissionFee !== undefined) ? fees.commissionFee : 0,
        ShippingFee: 0,
        CollectionFee: (fees && fees.collectionFee !== undefined) ? fees.collectionFee : 0,
        TotalMarketplaceFee: (fees && fees.totalMarketplaceFee !== undefined) ? fees.totalMarketplaceFee : 0,
        GstOnMarketplaceFee: (fees && fees.gstOnMarketplaceFee !== undefined) ? fees.gstOnMarketplaceFee : 0,
        TotalDeductionsFromSeller: (fees && fees.totalDeductions !== undefined) ? fees.totalDeductions : 0,
        SettlementValue: (fees && fees.settlementValue !== undefined) ? fees.settlementValue : 0,

        // Transaction Fields (Initially Empty)
        CustTransaction_No: "",
        CustUTR_No: "",
        RefundCustTxnNo: "",
        RefundCustUTR_No: "",


        // Additional Fields
        Hsn_Code: Number(item.hsn_code) || 85171200,
        Notes: "",
        CorierName: "",
        Awb_no: "",
        SellerInvoice: "",

        
      };

      // Store order items under deterministic product ID document to align with Android path
      await setDoc(doc(orderDocRef, 'OrderItems', String(item.product)), orderItem);

      // Reserve inventory
      await reserveInventory(item.product, Number(item.qty), userId);
    }

    // Add to user's order history
    if (userId && userId !== 'guest') {
      await setDoc(doc(collection(doc(collection(firestore, 'USERS'), userId), 'USER_ORDERS'), orderId), {
        order_id: orderId,
        Time: serverTimestamp(),
      });
      
      // Client-side notification for immediate feedback
      try {
        await createOrderPlacedNotification(userId, orderId);
      } catch (e) {
        console.warn('Client notification write failed:', e);
      }
    }

    // Remove only purchased items from user's cart in Firestore
    if (userId && userId !== 'guest') {
      await clearUserCart(userId, items);
    }

    // Remove only purchased items from Redux cart and localStorage
    try {
      const purchasedIds = items.map(it => it.product);
      purchasedIds.forEach(pid => {
        dispatch({ type: 'CART_REMOVE_ITEM', payload: pid });
      });
      const stateAfter = getState && typeof getState === 'function' ? getState() : null;
      const newItems = stateAfter?.cart?.cartItems || [];
      localStorage.setItem('cartItems', JSON.stringify(newItems));
    } catch (_) {}

    // If COD, mark order as Ordered immediately (Payment_Status remains Not Paid)
    if (isCOD) {
      await dispatch(updateCODOrderStatus(orderId));
    }

    // Include basic product title for success screen convenience
    const firstTitle = items[0]?.product_title || '';
    const successPayload = { orderId, paymentMethod, totalAmount, productTitle: firstTitle };
    console.log('✅ Order created successfully, dispatching success:', successPayload);
    dispatch({ type: ORDER_CREATE_SUCCESS, payload: successPayload });
    console.log('📤 ORDER_CREATE_SUCCESS dispatched');
  } catch (error) {
    console.error('❌ createOrder failed:', error);
    const message = error?.message || 'Failed to create order';
    dispatch({ type: ORDER_CREATE_FAIL, payload: message });
    console.log('📤 ORDER_CREATE_FAIL dispatched:', message);
  }
};

// Reserve inventory for ordered items
const reserveInventory = async (productId, quantity, userId) => {
  try {
    // Add to quantity tracking
    const quantityRef = doc(collection(doc(firestore, 'PRODUCTS', productId), 'QUANTITY'));
    await setDoc(quantityRef, {
      time: serverTimestamp(),
      user_ID: userId
    });

    // Update total ordered quantity
    const productRef = doc(firestore, 'PRODUCTS', productId);
    await updateDoc(productRef, {
      totalOrderQty: increment(quantity)
    });
  } catch (error) {
    console.error('Failed to reserve inventory:', error);
  }
};

// Clear user cart after successful order
const clearUserCart = async (userId, orderedItems) => {
  try {
    const cartRef = doc(firestore, 'USERS', userId, 'USER_DATA', 'MY_CART');
    const cartSnap = await getDoc(cartRef);
    if (!cartSnap.exists()) return;

    const cartData = cartSnap.data() || {};
    const orderedProductIds = new Set(orderedItems.map(item => String(item.product)));

    // Collect existing entries regardless of indexing style
    const entries = Object.keys(cartData)
      .filter(k => /^product_ID_\d+$/i.test(k))
      .map(k => {
        const idx = k.split('_').pop();
        const pid = String(cartData[k]);
        const qty = cartData[`quantity_${idx}`];
        return { pid, qty };
      });

    // Filter out purchased items
    const remaining = entries.filter(e => !orderedProductIds.has(String(e.pid)));

    // Rebuild 0-based structure
    const newCartData = { list_size: remaining.length };
    remaining.forEach((e, i) => {
      newCartData[`product_ID_${i}`] = e.pid;
      if (e.qty !== undefined) newCartData[`quantity_${i}`] = e.qty;
    });

    await setDoc(cartRef, newCartData, { merge: false });
  } catch (error) {
    console.error('Failed to clear cart:', error);
  }
};

// Update order status after payment success
export const updateOrderPaymentStatus = (orderId, paymentData) => async (dispatch) => {
  try {
    const orderRef = doc(firestore, 'ORDERS', orderId);
    const orderItemsRef = collection(orderRef, 'OrderItems');
    
    // Update main order document
    const orderUpdates = {
      Payment_Status: "Paid",
      Order_Status: "Ordered",
      ...paymentData
    };
    
    await updateDoc(orderRef, orderUpdates);
    
    // Update all order items
    const orderItemsSnap = await getDocs(orderItemsRef);
    const updatePromises = orderItemsSnap.docs.map(doc => 
      updateDoc(doc.ref, {
        Payment_Status: "Paid",
        Order_Status: "Ordered",
        ...paymentData
      })
    );
    
    await Promise.all(updatePromises);
    
    dispatch({ type: ORDER_PAYMENT_SUCCESS, payload: { orderId, paymentData } });
  } catch (error) {
    console.error('Failed to update payment status:', error);
  }
};

// Update COD order status after OTP verification
export const updateCODOrderStatus = (orderId) => async (dispatch) => {
  try {
    const orderRef = doc(firestore, 'ORDERS', orderId);
    const orderItemsRef = collection(orderRef, 'OrderItems');
    
    // Update main order document (Payment_Status remains "Not Paid" for COD)
    await updateDoc(orderRef, {
      Order_Status: "Ordered"
    });
    
    // Update all order items
    const orderItemsSnap = await getDocs(orderItemsRef);
    const updatePromises = orderItemsSnap.docs.map(doc => 
      updateDoc(doc.ref, {
        Order_Status: "Ordered"
      })
    );
    
    await Promise.all(updatePromises);
    
    dispatch({ type: ORDER_PAYMENT_SUCCESS, payload: { orderId, method: 'COD' } });
  } catch (error) {
    console.error('Failed to update COD order status:', error);
  }
};